import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { ThemeProvider } from 'styled-components';
import GroverPaymentSDK from 'groverPaymentSDK';

import { locationQuery as locationQuerySelector } from 'ggApp/modules/router/selectors';
import axios from 'ggApp/utils/requests';
import { promiseTimeout as sleepFor } from 'ggApp/utils/promise';
import SepaPaymentTerms from 'ggApp/modules/newCheckout/components/CheckoutStep/steps/Payment/SepaPaymentTerms';

import RedirectPreflight from './RedirectPreflight';
import GatewaySelector from './GatewaySelector';
import PaymentMethodPoller from './PaymentMethodPoller';
import { PaymentSdkContextProvider } from './state/paymentSdkContext';
import didReceiveRedirect from './utils/didReceiveRedirect';
import { useIsMounted } from './utils/isMounted';

const REDIRECT_PREFLIGHT_DURATION = 5000;

const shouldShowRedirectPreflight = (gatewayType) => {
    return ['sepa', 'paypal'].includes(gatewayType);
};

const sdkInstance = new GroverPaymentSDK(axios);
const mapStateToProps = createStructuredSelector({
    locationQuery: locationQuerySelector,
});

const GatewayFlowContainer = ({
    onPaymentMethodCreated,
    onLegacyPaymentMethodCreated,
    onRedirectPreflight,
    getRedirectPreflightComponent,
    locationQuery,
    children,
    darkTheme,
    uiComponent,
    orderNumber,
    paymentTermsInfo,
    setPaymentTermsInfo,
}) => {
    const [redirectPreflightInfo, setRedirectPreflightInfo] = useState(null);
    const showPolling = didReceiveRedirect(locationQuery);
    const showPreflight = !!redirectPreflightInfo;
    const showPaymentTerms = !!paymentTermsInfo && !showPreflight;
    const showSelector = !showPolling && !showPreflight && !showPaymentTerms;

    const isMounted = useIsMounted();

    const handleMethodCreated = useCallback(
        (result, billingAccountId) => {
            if (result?.redirect) {
                Promise.resolve()
                    .then(() => {
                        const preflight = shouldShowRedirectPreflight(result.redirect.gatewayType);
                        if (preflight) {
                            setRedirectPreflightInfo(result.redirect);
                            onRedirectPreflight();
                            return sleepFor(REDIRECT_PREFLIGHT_DURATION);
                        }
                        return Promise.resolve();
                    })
                    .then(() => {
                        if (isMounted.current) {
                            window.location.href = result.redirect.url;
                        }
                    });
            } else {
                onPaymentMethodCreated(result, billingAccountId);
            }
        },
        [onPaymentMethodCreated, onRedirectPreflight, isMounted],
    );

    const theme = {
        mode: darkTheme ? 'dark' : 'light',
    };

    return (
        <PaymentSdkContextProvider value={sdkInstance}>
            <ThemeProvider theme={theme}>
                {showPaymentTerms && (
                    <SepaPaymentTerms
                        paymentTermsInfo={paymentTermsInfo}
                        setPaymentTermsInfo={setPaymentTermsInfo}
                        onPaymentMethodCreated={handleMethodCreated}
                    />
                )}
                {showPreflight && (
                    <RedirectPreflight
                        gatewayType={redirectPreflightInfo.gatewayType}
                        getComponent={getRedirectPreflightComponent}
                    />
                )}

                {showPolling && (
                    <PaymentMethodPoller
                        onPaymentMethodCreated={handleMethodCreated}
                        onLegacyPaymentMethodCreated={onLegacyPaymentMethodCreated}
                    >
                        {children}
                    </PaymentMethodPoller>
                )}

                {showSelector && (
                    <GatewaySelector
                        orderNumber={orderNumber}
                        onPaymentMethodCreated={handleMethodCreated}
                        onLegacyPaymentMethodCreated={onLegacyPaymentMethodCreated}
                        uiComponent={uiComponent}
                        paymentTermsInfo={paymentTermsInfo}
                        setPaymentTermsInfo={setPaymentTermsInfo}
                    >
                        {children}
                    </GatewaySelector>
                )}
            </ThemeProvider>
        </PaymentSdkContextProvider>
    );
};

GatewayFlowContainer.propTypes = {
    onPaymentMethodCreated: PropTypes.func,
    onLegacyPaymentMethodCreated: PropTypes.func,
    onRedirectPreflight: PropTypes.func,
    getRedirectPreflightComponent: PropTypes.func,
    locationQuery: PropTypes.string.isRequired,
    children: PropTypes.node,
    darkTheme: PropTypes.bool,
};

GatewayFlowContainer.defaultProps = {
    onPaymentMethodCreated: () => {},
    onLegacyPaymentMethodCreated: () => {},
    onRedirectPreflight: () => {},
    getRedirectPreflightComponent: () => {},
    children: null,
    darkTheme: false,
};

export default connect(mapStateToProps)(GatewayFlowContainer);
